import {
  INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
  INSPECTION_PORTAL_IMPERSONATE_USER_ID_HEADER,
} from "@/constants";
import { formatAddressParts } from "@/lib/address-format";
import { getHeraApiBaseUrl } from "@/lib/hera-api-base-url";
import { trackedFetch } from "@/lib/server-request-activity";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";

export type InspectionPortalMode = "requires" | "complete" | "all";

export type InspectionAssigneeInfo = {
  id: string;
  email: string;
  name: string | null;
};

export type InspectionPortalLicenseItem = {
  license_id: string;
  display_id: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  latitude: number | null;
  longitude: number | null;
  jurisdiction_id: string;
  jurisdiction_name: string | null;
  jurisdiction_logo_gcs_key: string | null;
  jurisdiction_theme_color: string | null;
  pending_inspection_id: string | null;
  latest_completed_inspection_id: string | null;
  registrant_account_id: string | null;
  /** Distinct assignees on pending inspection(s) for this license. */
  assignees?: InspectionAssigneeInfo[];
};

export type InspectionOrgUser = {
  id: string;
  email: string;
  name: string | null;
  user_type: string;
  license_access_mode: string | null;
};

export type InspectionImpersonationCandidate = {
  login_identity_id: string;
  email: string;
  name: string | null;
  user_type: string;
  is_assignee: boolean;
  registrant_account_id: string | null;
};

function apiBase(): string {
  return getHeraApiBaseUrl().replace(/\/$/, "");
}

export function mergeInspectionImpersonationHeaders(
  headers: HeadersInit | undefined,
): Headers {
  const h = new Headers(headers);
  const inspectionAccountId = sessionStorage.get<string>(
    SESSION_KEYS.INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  // Registrant-portal operator impersonation uses a separate session key; reuse it
  // so Inspections-tab API calls and Perform CTAs carry the org into /inspection/*.
  const registrantAccountId = sessionStorage.get<string>(
    SESSION_KEYS.REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  const accountId = inspectionAccountId.trim() || registrantAccountId.trim();
  if (accountId) {
    h.set(INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER, accountId);
  }
  const userId = sessionStorage.get<string>(
    SESSION_KEYS.INSPECTION_PORTAL_IMPERSONATE_USER_ID,
    "",
  );
  if (userId.trim()) {
    h.set(INSPECTION_PORTAL_IMPERSONATE_USER_ID_HEADER, userId.trim());
  }
  return h;
}

async function inspectionFetch<T>(
  path: string,
  getAccessToken: () => Promise<string>,
  init?: RequestInit,
): Promise<T> {
  const token = await getAccessToken();
  const headers = mergeInspectionImpersonationHeaders(init?.headers);
  headers.set("Accept", "application/json");
  if (init?.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Authorization", `Bearer ${token}`);

  const response = await trackedFetch(`${apiBase()}${path}`, {
    ...init,
    headers,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      detail || `Request failed (${response.status}) for ${path}`,
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function formatInspectionAddress(
  item: Pick<
    InspectionPortalLicenseItem,
    "address1" | "city" | "state" | "zipcode"
  >,
): string {
  return formatAddressParts(item) || "Unknown address";
}

/** Prefer name, fall back to email; join multiple assignees with commas. */
export function formatInspectionAssignees(
  assignees: InspectionAssigneeInfo[] | null | undefined,
): string {
  if (!assignees?.length) return "—";
  const labels = assignees
    .map((a) => {
      const name = a.name?.trim();
      if (name) return name;
      const email = a.email?.trim();
      return email || null;
    })
    .filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join(", ") : "—";
}

export async function listInspectionPortalLicenses(
  getAccessToken: () => Promise<string>,
  mode: InspectionPortalMode = "requires",
): Promise<InspectionPortalLicenseItem[]> {
  const params = new URLSearchParams({ mode });
  return inspectionFetch(
    `/inspection/licenses?${params.toString()}`,
    getAccessToken,
  );
}

export async function listInspectionDispatchLicenses(
  getAccessToken: () => Promise<string>,
): Promise<InspectionPortalLicenseItem[]> {
  return inspectionFetch("/inspection/dispatch/licenses", getAccessToken);
}

export async function bulkAssignInspections(
  getAccessToken: () => Promise<string>,
  body: { license_ids: string[]; assignee_id: string },
): Promise<{ updated_inspection_ids: string[] }> {
  return inspectionFetch("/inspection/assign", getAccessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Ensure the current user can open licenses in the inspection portal. */
export async function ensureInspectionLicenseAccess(
  getAccessToken: () => Promise<string>,
  body: { license_ids: string[] },
): Promise<{ ok: boolean; license_access_mode: string | null }> {
  return inspectionFetch("/inspection/ensure-access", getAccessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function uploadInspectionAnswerFile(
  getAccessToken: () => Promise<string>,
  file: File,
): Promise<{ token: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return inspectionFetch("/inspection/answer_files/upload", getAccessToken, {
    method: "POST",
    body: formData,
  });
}

/** Trusted server clock for photo capture time integrity (NTP-lite client sync). */
export async function getInspectionServerTime(
  getAccessToken: () => Promise<string>,
): Promise<{ server_time: string; unix_ms: number }> {
  return inspectionFetch("/inspection/time", getAccessToken);
}

export async function listInspectionOrgUsers(
  getAccessToken: () => Promise<string>,
  registrantAccountId?: string,
): Promise<InspectionOrgUser[]> {
  const params = new URLSearchParams();
  if (registrantAccountId) {
    params.set("registrant_account_id", registrantAccountId);
  }
  const query = params.toString();
  return inspectionFetch(
    `/inspection/org_users${query ? `?${query}` : ""}`,
    getAccessToken,
  );
}

export async function inviteInspectionUser(
  getAccessToken: () => Promise<string>,
  body: { email: string; name: string; license_ids?: string[] },
): Promise<{ id: string; email: string }> {
  return inspectionFetch("/inspection/users", getAccessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function disableInspectionUser(
  getAccessToken: () => Promise<string>,
  userId: string,
): Promise<{ ok: boolean }> {
  return inspectionFetch(
    `/inspection/users/${userId}/disable`,
    getAccessToken,
    {
      method: "POST",
    },
  );
}

export async function listInspectionImpersonationCandidates(
  getAccessToken: () => Promise<string>,
  licenseId: string,
): Promise<InspectionImpersonationCandidate[]> {
  // Operator SPA uses the operator audience; call the operator route (not /inspection/*).
  return inspectionFetch(
    `/operator/licenses/${licenseId}/inspection-impersonation-candidates`,
    getAccessToken,
  );
}

export async function unassignInspection(
  getAccessToken: () => Promise<string>,
  inspectionId: string,
): Promise<{ ok: boolean }> {
  return inspectionFetch(
    `/inspection/inspections/${inspectionId}/unassign`,
    getAccessToken,
    { method: "POST" },
  );
}

export async function fetchAuthenticatedAnswerFileBlobUrl(
  getAccessToken: () => Promise<string>,
  token: string,
): Promise<string> {
  const accessToken = await getAccessToken();
  const response = await trackedFetch(
    `${apiBase()}/shared/answer_files/${token}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load file (${response.status})`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
